<template>
  <div class="google-sign-in">
    <div
      ref="buttonHost"
      class="google-sign-in__host"
      :class="{ 'google-sign-in__host--disabled': disabled || loading }"
      :aria-busy="loading ? 'true' : 'false'"
    ></div>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="tertiary"
      height="2"
      class="google-sign-in__progress"
    />
    <p v-if="errorMessage" class="google-sign-in__error" role="status">
      {{ errorMessage }}
      <button type="button" class="google-sign-in__retry" @click="renderGoogleButton">
        {{ $t("auth.google.retry") }}
      </button>
    </p>
  </div>
</template>

<script>
import axios from "axios";
import { loadGoogleIdentityServices } from "@/Utile/googleIdentityServices.mjs";

export default {
  name: "GoogleSignInButton",
  props: {
    clientId: { type: String, required: true },
    locale: { type: String, default: "ro" },
    disabled: { type: Boolean, default: false },
  },
  emits: ["credential", "ready", "error"],
  data() {
    return { loading: true, errorMessage: "", nonce: "", googleApi: null };
  },
  watch: {
    locale() {
      this.renderButtonMarkup();
    },
  },
  mounted() {
    this.renderGoogleButton();
  },
  beforeUnmount() {
    this.googleApi?.cancel?.();
  },
  methods: {
    async renderGoogleButton() {
      if (!this.clientId || !this.$refs.buttonHost) return;
      this.loading = true;
      this.errorMessage = "";
      try {
        const [googleApi, nonceResponse] = await Promise.all([
          loadGoogleIdentityServices(),
          axios.get("/auth/google/nonce", { withCredentials: true }),
        ]);
        this.googleApi = googleApi;
        this.nonce = String(nonceResponse?.data?.nonce || "");
        if (!this.nonce) throw new Error("Google nonce is missing");
        googleApi.initialize({
          client_id: this.clientId,
          callback: this.handleCredentialResponse,
          nonce: this.nonce,
          ux_mode: "popup",
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        this.$refs.buttonHost.replaceChildren();
        const width = Math.max(200, Math.min(400, this.$refs.buttonHost.clientWidth || 360));
        googleApi.renderButton(this.$refs.buttonHost, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          logo_alignment: "left",
          locale: this.locale,
          width,
        });
        this.$emit("ready");
      } catch (error) {
        this.errorMessage = this.$t("auth.google.errors.unavailable");
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
    renderButtonMarkup() {
      if (!this.googleApi || !this.$refs.buttonHost) return;
      this.$refs.buttonHost.replaceChildren();
      const width = Math.max(200, Math.min(400, this.$refs.buttonHost.clientWidth || 360));
      this.googleApi.renderButton(this.$refs.buttonHost, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        logo_alignment: "left",
        locale: this.locale,
        width,
      });
    },
    handleCredentialResponse(response) {
      const credential = String(response?.credential || "");
      if (!credential || !this.nonce || this.disabled) return;
      const nonce = this.nonce;
      this.nonce = "";
      this.$emit("credential", { credential, nonce });
    },
  },
};
</script>

<style scoped>
.google-sign-in {
  position: relative;
  width: 100%;
}

.google-sign-in__host {
  display: flex;
  min-height: 44px;
  width: 100%;
  align-items: center;
  justify-content: center;
}

.google-sign-in__host--disabled {
  pointer-events: none;
  opacity: 0.55;
}

.google-sign-in__progress {
  margin-top: 2px;
}

.google-sign-in__error {
  margin: 8px 0 0;
  color: rgb(var(--v-theme-error));
  font-size: 12px;
  text-align: center;
}

.google-sign-in__retry {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
}
</style>
